import { useDispatch, useSelector } from 'react-redux';
import { Badge, Box, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import { useAuth } from '../../../contexts/authContext';

import { GameStatus } from '../../../constants/gameConstants';
import ColorLine from '../colorDot/colorLine';
import EmojiOutcomeBox from '../playerDisplay/emojiOutcomeBox';
import EmojiMiniOutcomeBox from '../playerDisplay/emojiMiniOutcomeBox';

const TeamMiniFinalScore = ({ team, game }) => {
  const useTeamName = team.name !== 'Team 1' && team.name !== 'Team 2' && team.name;

  const teamName = useTeamName
    ? team.name
    : team.players
        .map((player) => {
          if (team.players.length > 2) {
            return player.split(' ')[0].charAt(0) + '. ' + player.split(' ')[1];
          } else {
            return player;
          }
        })
        .join(', ');

  return (
    <Box display="flex" flexDirection={'column'} alignItems="center">
      <ColorLine color={team.color} />
      <Stack px={1} alignItems={'center'} justifyContent={'center'}>
        <Typography variant="caption">{teamName}</Typography>
        {game.teamTotals && (
          <EmojiMiniOutcomeBox
            score={game.teamTotals[team.id]}
            emoji={
              game.winningTeamId === team.id ? '🏆' : game.winningTeamId === null ? '🏅' : '🥈'
            }
          />
        )}
      </Stack>
    </Box>
  );
};

const MiniScoreBox = ({ score, emoji, teamTotal }) => {
  return (
    <Box display="flex" justifyContent={'center'} alignItems={'center'}>
      <Badge
        badgeContent={emoji}
        showZero={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}>
        <Typography variant="h5" role="img" aria-label="Trophy">
          {score} <Typography variant="caption">{teamTotal && `(${teamTotal})`}</Typography>
        </Typography>
      </Badge>
    </Box>
  );
};

const TeamMiniEndScore = ({ team, game }) => {
  const currentSet = game.sets.find((set) => set.id === game.currentSet);
  const getEndScore = (team) => {
    let score = 0;
    if (currentSet) {
      //Calculate all end scores for the team
      for (let i = 0; i < currentSet.scores.length; i++) {
        const end = currentSet.scores[i];
        if (end.teamScores[team]) {
          score += end.teamScores[team];
        }
      }
    }

    return score;
  };

  const useTeamName = team.name !== 'Team 1' && team.name !== 'Team 2' && team.name;

  const teamName = useTeamName
    ? team.name
    : team.players
        .map((player) => {
          if (team.players.length > 2) {
            return player.split(' ')[0].charAt(0) + '. ' + player.split(' ')[1];
          } else {
            return player;
          }
        })
        .join(', ');

  return (
    <Box
      display="flex"
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems="center"
      minHeight={60}>
      {/* <ColorLine color={team.color} /> */}
      <Stack alignItems={'center'} justifyContent={'center'}>
        <Typography variant="caption" textAlign="center">
          {teamName}
        </Typography>
        {game.status === GameStatus.IN_PROGRESS && currentSet.isDone ? (
          currentSet.winningId ? (
            <MiniScoreBox
              teamTotal={game.teamTotals[team.id]}
              score={getEndScore(team.id)}
              emoji={currentSet.winningId === team.id ? '🏆' : '🥈'}
            />
          ) : (
            <MiniScoreBox teamTotal={game.teamTotals[team.id]} score={getEndScore(team.id)} />
          )
        ) : null}

        {game.status === GameStatus.COMPLETE && (
          <MiniScoreBox
            teamTotal={game.teamTotals[team.id]}
            score={game.teamTotals[team.id]}
            emoji={game.winningTeamId === team.id ? '🏆' : '🥈'}
          />
        )}

        {game.status === GameStatus.PLAYOFF && game.playoff && (
          <MiniScoreBox
            teamTotal={game.teamTotals[team.id]}
            score={game.playoff.teamScores[team.id]}
          />
        )}

        {game.status === GameStatus.IN_PROGRESS && !currentSet.isDone && (
          <MiniScoreBox teamTotal={game.teamTotals[team.id]} score={getEndScore(team.id)} />
        )}
      </Stack>
    </Box>
  );
};

const GameMiniCard = ({ game }) => {
  const currentSet = game.sets.find((set) => set.id === game.currentSet);

  const getElementByGameStatus = (team) => {
    switch (game.status) {
      case GameStatus.COMPLETE:
        return <TeamMiniFinalScore team={team} game={game} />;
      case GameStatus.IN_PROGRESS:
        return currentSet && currentSet.isDone && currentSet.winningId ? (
          <TeamMiniFinalScore team={team} game={game} />
        ) : (
          <TeamMiniEndScore team={team} game={game} />
        );
      case GameStatus.PLAYOFF:
        return <TeamMiniEndScore team={team} game={game} />;
      default:
        return <TeamMiniEndScore team={team} game={game} />;
    }
  };

  return (
    <Paper
      sx={{
        width: 230,
        p: 1,
        borderRadius: 2
      }}
      elevation={3}>
      <Stack justifyContent={'space-between'} height="100%">
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'center'}
          alignItems={'center'}
          width={'100%'}>
          <Typography textAlign={'center'}>{game.name ? game.name : 'Private Game'}</Typography>
        </Box>

        <Box
          display="flex"
          flexDirection="row"
          justifyContent={'space-evenly'}
          alignItems={'center'}
          gap={1}>
          {getElementByGameStatus(game.teams.teamA)}
          <Typography>VS</Typography>
          {getElementByGameStatus(game.teams.teamB)}
        </Box>
      </Stack>
    </Paper>
  );
};

export default GameMiniCard;
